import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuthContext } from "../lib/authLib";
import { User, get as getUser } from "../lib/userLib";
import { Message, getPageForInbox } from "../lib/messagesLib";
import { Inbox } from "../lib/inboxLib";
import { useSnackbar } from "../contexts/SnackbarContext";
import MessagesCard from "./dashboard/MessagesCard";

const PAGE_SIZE = 10;

const Messages: React.FC = () => {
  const { user: authUser } = useAuthContext();
  const { showSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get("userId");
  const filterParam = searchParams.get("filter") || "";
  const inboxParam = searchParams.get("inbox") || "all";
  // Convert 1-indexed URL param to 0-indexed internal state (page=2 means currentPage=1)
  const pageParamFromUrl = searchParams.get("page");
  const pageParam = pageParamFromUrl ? parseInt(pageParamFromUrl, 10) - 1 : 0;

  const [user, setUser] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(pageParam);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState(filterParam);
  const [debouncedFilter, setDebouncedFilter] = useState(filterParam);
  const [selectedInboxId, setSelectedInboxId] = useState<string | "all">(
    inboxParam
  );
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const filterChangeRef = useRef(false);
  const pageChangeRef = useRef(false);
  const inboxChangeRef = useRef(false);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      const targetUserId = userId || authUser?.id;
      if (!targetUserId) {
        setIsLoading(false);
        return;
      }

      try {
        const userData = await getUser(targetUserId);
        setUser(userData);
      } catch (error) {
        console.error("Error fetching user data:", error);
        showSnackbar("Failed to load user data", "error");
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [userId, authUser?.id, showSnackbar]);

  // Fetch messages based on selected inbox, filter, and page
  const fetchMessages = useCallback(
    async (inboxId: string | "all", filter: string = "", page: number = 0) => {
      console.log("[Messages] fetchMessages called", {
        inboxId,
        filter,
        page,
        hasUser: !!user,
        inboxCount: user?.inboxes?.length,
      });

      if (!user || !user.inboxes || user.inboxes.length === 0) {
        console.log("[Messages] No user or inboxes, clearing messages");
        setMessages([]);
        setTotalCount(0);
        setIsLoading(false);
        setIsLoadingMessages(false);
        return;
      }

      console.log(
        "[Messages] Starting fetch, setting isLoadingMessages to true"
      );
      setIsLoadingMessages(true);
      // Don't clear messages here - keep showing previous page while loading

      try {
        let allMessages: Message[] = [];
        let total = 0;

        if (inboxId === "all") {
          console.log("[Messages] Fetching from all inboxes", {
            inboxCount: user.inboxes.length,
            page,
          });
          // Fetch messages from all inboxes
          // Note: When "all" is selected, we can't use server-side pagination effectively
          // For now, we'll fetch page 0 from each inbox and combine
          // TODO: Consider if we need to handle pagination differently for "all inboxes"
          const messagePromises = user.inboxes.map((inbox) =>
            getPageForInbox(inbox.id, false, filter, page).catch((error) => {
              console.error(
                `Error fetching messages for inbox ${inbox.id}:`,
                error
              );
              return {
                messages: [],
                totalCount: 0,
                page: 0,
                inboxId: inbox.id,
              };
            })
          );
          const responses = await Promise.all(messagePromises);
          console.log("[Messages] Received responses from all inboxes", {
            responseCount: responses.length,
            totalMessagesBeforeSlice: responses.flatMap((r) => r.messages)
              .length,
          });
          allMessages = responses.flatMap((r) => r.messages);
          total = responses.reduce((sum, r) => sum + r.totalCount, 0);
        } else {
          console.log("[Messages] Fetching from specific inbox", {
            inboxId,
            page,
          });
          // Fetch messages from specific inbox with server-side pagination
          const response = await getPageForInbox(inboxId, false, filter, page);
          allMessages = response.messages;
          total = response.totalCount;
          console.log("[Messages] Received response from specific inbox", {
            messageCount: allMessages.length,
            totalCount: total,
          });
        }

        console.log("[Messages] Setting messages state", {
          messageCount: allMessages.length,
          totalCount: total,
        });
        setMessages(allMessages);
        setTotalCount(total);
        setIsLoadingMessages(false);
        console.log(
          "[Messages] Fetch complete, isLoadingMessages set to false"
        );
      } catch (error) {
        console.error("[Messages] Error fetching messages:", error);
        showSnackbar("Failed to load messages", "error");
        setIsLoadingMessages(false);
      }
    },
    [user, showSnackbar]
  );

  // Debounce search query updates
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedFilter(searchQuery);
    }, 500); // 500ms debounce delay

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Update URL when debounced filter changes (reset to page 0)
  // Only fire when filter actually changes, not when other URL params change
  const previousFilterRef = useRef(debouncedFilter);
  useEffect(() => {
    if (previousFilterRef.current === debouncedFilter) {
      return; // Filter hasn't actually changed
    }
    previousFilterRef.current = debouncedFilter;

    console.log("[Messages] Filter changed, resetting to page 0", {
      debouncedFilter,
      currentPage,
    });
    filterChangeRef.current = true;
    const params = new URLSearchParams(searchParams);
    if (debouncedFilter.trim()) {
      params.set("filter", debouncedFilter);
    } else {
      params.delete("filter");
    }
    // Reset to page 0 when filter changes (omit page param for first page)
    params.delete("page");
    setCurrentPage(0);
    navigate({ search: params.toString() }, { replace: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilter, navigate]);

  // Update page in URL (1-indexed, only show if not first page)
  // Skip if it was triggered by a filter or inbox change
  const previousPageRef = useRef(currentPage);
  useEffect(() => {
    if (previousPageRef.current === currentPage) {
      return; // Page hasn't actually changed
    }
    previousPageRef.current = currentPage;

    if (filterChangeRef.current || inboxChangeRef.current) {
      filterChangeRef.current = false;
      inboxChangeRef.current = false;
      return;
    }

    console.log("[Messages] CurrentPage changed, updating URL", {
      currentPage,
      pageParam,
    });
    pageChangeRef.current = true;
    const params = new URLSearchParams(searchParams);
    if (currentPage === 0) {
      // Omit page param for first page
      params.delete("page");
    } else {
      // Convert 0-indexed to 1-indexed for URL (page 1 -> page=2)
      params.set("page", (currentPage + 1).toString());
    }
    navigate({ search: params.toString() }, { replace: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, navigate]);

  // Reset to page 0 when inbox changes
  // Only fire when inbox actually changes, not when other URL params change
  const previousInboxRef = useRef(selectedInboxId);
  useEffect(() => {
    if (previousInboxRef.current === selectedInboxId) {
      return; // Inbox hasn't actually changed
    }
    previousInboxRef.current = selectedInboxId;

    console.log("[Messages] Inbox changed, resetting to page 0", {
      selectedInboxId,
    });
    inboxChangeRef.current = true;
    setCurrentPage(0);
    const params = new URLSearchParams(searchParams);
    params.delete("page"); // Omit page param for first page
    navigate({ search: params.toString() }, { replace: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInboxId, navigate]);

  // Fetch messages when user, selected inbox, debounced filter, or page changes
  useEffect(() => {
    if (user) {
      console.log("[Messages] Fetching messages", {
        inboxId: selectedInboxId,
        filter: debouncedFilter,
        page: currentPage,
      });
      fetchMessages(selectedInboxId, debouncedFilter, currentPage);
    }
  }, [user, selectedInboxId, debouncedFilter, currentPage, fetchMessages]);

  // Update search query when URL param changes (initial load)
  useEffect(() => {
    console.log("[Messages] Filter param changed from URL", { filterParam });
    setSearchQuery(filterParam);
    setDebouncedFilter(filterParam);
  }, [filterParam]);

  // Update selected inbox when URL param changes
  useEffect(() => {
    console.log("[Messages] Inbox param changed from URL", { inboxParam });
    setSelectedInboxId(inboxParam);
  }, [inboxParam]);

  // Update current page when URL param changes (e.g., browser back/forward)
  // Only update if it wasn't triggered by our own page change
  useEffect(() => {
    if (pageChangeRef.current) {
      pageChangeRef.current = false;
      return; // This was our own change, ignore it
    }

    console.log("[Messages] Page param changed from URL", {
      pageParam,
      currentPage,
    });
    if (pageParam !== currentPage) {
      setCurrentPage(pageParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageParam]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleReset = () => {
    setSearchQuery("");
    setDebouncedFilter("");
  };

  const handleInboxChange = (inboxId: string | "all") => {
    console.log("[Messages] handleInboxChange called", { inboxId });
    setSelectedInboxId(inboxId);
    setCurrentPage(0); // Reset to page 0 when changing inbox
    const params = new URLSearchParams(searchParams);
    if (inboxId === "all") {
      params.delete("inbox");
    } else {
      params.set("inbox", inboxId);
    }
    params.delete("page"); // Omit page param for first page
    navigate({ search: params.toString() }, { replace: false });
  };

  const handlePageChange = (newPage: number) => {
    console.log("[Messages] handlePageChange called", {
      newPage,
      currentPage,
      pageParam,
    });
    setCurrentPage(newPage);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  if (isLoading && !user) {
    return <div className="text-center p-8">Loading...</div>;
  }

  if (!user) {
    return <div className="text-center p-8">No user data available</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Messages</h2>
        <div className="flex items-center">
          <span className="mr-3 text-sm text-gray-600 dark:text-gray-300">
            Inbox:
          </span>
          <div className="flex border border-gray-300 dark:border-gray-600 rounded-full overflow-hidden bg-gray-50 dark:bg-gray-800">
            <button
              className={`px-3 py-1 text-sm transition-colors focus:outline-none ${
                selectedInboxId === "all"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
              } rounded-l-full`}
              onClick={() => handleInboxChange("all")}
            >
              All inboxes
            </button>
            {user.inboxes?.map((inbox: Inbox, idx: number) => (
              <button
                key={inbox.id}
                className={`px-3 py-1 text-sm transition-colors focus:outline-none ${
                  selectedInboxId === inbox.id
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
                } ${idx === user.inboxes.length - 1 ? "rounded-r-full" : ""}`}
                onClick={() => handleInboxChange(inbox.id)}
              >
                {inbox.displayName || inbox.email}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search Box */}
      <div className="mb-4 flex items-center gap-2">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search (e.g., from:gmail.com, subject:hello, id:xxx)"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
        {searchQuery && (
          <button
            onClick={handleReset}
            className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline"
          >
            Reset
          </button>
        )}
      </div>

      {/* Search Results Info */}
      {debouncedFilter && (
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          {isLoadingMessages ? (
            <span>Loading messages...</span>
          ) : messages.length === 0 ? (
            <span>No matches found for "{debouncedFilter}"</span>
          ) : (
            <span>
              Showing message
              {messages.length !== 1 ? "s" : ""}
              {` matching "${debouncedFilter}"`}
            </span>
          )}
        </div>
      )}

      {/* Messages List */}
      {isLoadingMessages ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
          <div className="text-center text-gray-500 dark:text-gray-400">
            Loading messages...
          </div>
        </div>
      ) : (
        <>
          {console.log("[Messages] Rendering MessagesCard", {
            messagesCount: messages.length,
            currentPage,
            totalPages,
            totalCount,
            isLoadingMessages,
            messages: messages.map((m) => ({ id: m.id, subject: m.subject })),
          })}
          <MessagesCard
            messages={messages}
            showSnackbar={showSnackbar}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

export default Messages;
